import React, { useEffect, useState } from 'react';
import {
  Paper, Box, Tabs, Tab, Typography, CircularProgress, Alert,
  List, ListItem, ListItemText, Chip, Divider, Button, Snackbar, Stack,
} from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import SendIcon from '@mui/icons-material/Send';
import ReactMarkdown from 'react-markdown';
import api from '../../services/api';

function formatDate(d) {
  try { return new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }); }
  catch { return ''; }
}

function SummaryTab({ conversationId, token }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    if (!conversationId) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const data = await api.getTherapistSessionSummary(conversationId, token);
        if (!cancelled) setSummary(data);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Could not load summary');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [conversationId, token]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress size={22} /></Box>;
  if (error)   return <Alert severity="info" sx={{ m: 2 }}>{error}</Alert>;
  const text = summary?.therapistSummary || summary?.summary || summary?.clientSummary;
  if (!text) return <Alert severity="info" sx={{ m: 2 }}>No summary generated yet for the latest conversation.</Alert>;

  return (
    <Box sx={{ p: 2, fontSize: 14, '& p': { mb: 1 }, '& h1, & h2, & h3': { fontSize: 15, mb: 0.5, mt: 1 } }}>
      <ReactMarkdown>{text}</ReactMarkdown>
    </Box>
  );
}

function PreSessionTab({ clientId, token }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [assignments, setAssignments] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/pre-session/assignments/client/${clientId}`, token);
        if (!cancelled) setAssignments(res?.data?.assignments || []);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Could not load pre-session');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [clientId, token]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress size={22} /></Box>;
  if (error)   return <Alert severity="info" sx={{ m: 2 }}>{error}</Alert>;
  const open = assignments.filter(a => a.status !== 'completed' && a.status !== 'cancelled');
  if (open.length === 0) return <Alert severity="success" sx={{ m: 2 }}>No outstanding pre-session tasks.</Alert>;

  return (
    <Box sx={{ p: 1 }}>
      {open.map((a) => {
        const completionsById = new Map((a.taskCompletions || []).map(c => [String(c.taskId), c]));
        const tasks = a.templateSnapshot?.tasks || [];
        return (
          <Box key={a._id} sx={{ mb: 1.5 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{a.templateSnapshot?.name || 'Pre-session'}</Typography>
            <List dense disablePadding>
              {tasks.map((t) => {
                const c = completionsById.get(String(t._id));
                const done = c?.status === 'completed';
                const score = c?.result?.score;
                return (
                  <ListItem key={String(t._id)} disableGutters sx={{ pl: 1 }}>
                    <ListItemText
                      primary={`${t.title}${t.required === false ? ' (optional)' : ''}${typeof score === 'number' ? ` — score ${score}` : ''}`}
                      secondary={done ? 'Completed' : 'Outstanding'}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption', color: done ? 'success.main' : 'warning.main' }}
                    />
                  </ListItem>
                );
              })}
            </List>
          </Box>
        );
      })}
    </Box>
  );
}

function GoalsTab({ clientId, token }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [goals, setGoals] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.getTherapistClientGoals(clientId, token);
        const arr = Array.isArray(data) ? data : (data?.goals || []);
        if (!cancelled) setGoals(arr);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Could not load goals');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [clientId, token]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress size={22} /></Box>;
  if (error)   return <Alert severity="info" sx={{ m: 2 }}>{error}</Alert>;

  const active = goals.filter(g => !g.completedAt && g.status !== 'completed');
  const completed = goals.filter(g => g.completedAt || g.status === 'completed');

  if (active.length === 0 && completed.length === 0) {
    return <Alert severity="info" sx={{ m: 2 }}>No goals yet.</Alert>;
  }

  const render = (list, heading, tone) => list.length > 0 && (
    <Box sx={{ px: 2, pt: 1.5 }}>
      <Typography variant="caption" color="text.secondary">{heading}</Typography>
      <List dense disablePadding>
        {list.slice(0, 8).map((g) => (
          <ListItem key={g._id} sx={{ px: 0 }}>
            <ListItemText
              primary={<Typography variant="body2">{g.title || g.description}</Typography>}
              secondary={g.description && g.title ? (
                <Typography variant="caption" color="text.secondary" noWrap>
                  {g.description}
                </Typography>
              ) : null}
            />
            <Chip label={tone === 'success' ? 'done' : g.status || 'active'} size="small" color={tone} variant="outlined" />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box>
      {render(active, 'Active', 'primary')}
      {active.length > 0 && completed.length > 0 && <Divider sx={{ my: 1 }} />}
      {render(completed, 'Completed', 'success')}
    </Box>
  );
}

function CheckInsTab({ clientId, token }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.getClientCheckIns(clientId, token);
        const arr = data?.checkIns || data?.items || (Array.isArray(data) ? data : []);
        if (!cancelled) setItems(arr.slice(0, 8));
      } catch (err) {
        if (!cancelled) setError(err.message || 'Could not load check-ins');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [clientId, token]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress size={22} /></Box>;
  if (error)   return <Alert severity="info" sx={{ m: 2 }}>{error}</Alert>;
  if (items.length === 0) return <Alert severity="info" sx={{ m: 2 }}>No check-ins recorded.</Alert>;

  return (
    <List dense sx={{ px: 1 }}>
      {items.map((c) => (
        <ListItem key={c._id} sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
          <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              {formatDate(c.createdAt || c.date)}
            </Typography>
            {typeof c.moodScore === 'number' && (
              <Chip
                label={`Mood ${c.moodScore.toFixed(1)}`}
                size="small"
                color={c.moodScore >= 4 ? 'success' : c.moodScore >= 2.5 ? 'warning' : 'error'}
                variant="outlined"
              />
            )}
          </Box>
          {c.note && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {c.note}
            </Typography>
          )}
        </ListItem>
      ))}
    </List>
  );
}

/**
 * Tools tab — lets the therapist trigger an assessment to the client mid-call.
 *
 * Lists the therapist's effective assessments (defaults ∪ grants) from the
 * 3-tier governance resolver, then POSTs to
 *   /api/calls/:liveCallId/in-session/trigger-assessment
 * which the CallRoomPage already handles to display the assessment to the
 * client in real time.
 */
function ToolsTab({ liveCallId, token }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tests, setTests] = useState([]);
  const [pending, setPending] = useState({}); // testKey → boolean
  const [toast, setToast] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.get('/assessment-governance/therapist/available', token);
        const list = data?.testDefinitions || data?.data?.testDefinitions || [];
        if (!cancelled) setTests(list);
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.error || err.message || 'Could not load assessments');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const send = async (testKey) => {
    if (!liveCallId) {
      setToast({ message: 'Call not connected yet — try again in a moment.', severity: 'warning' });
      return;
    }
    setPending(prev => ({ ...prev, [testKey]: true }));
    try {
      await api.post(`/calls/${liveCallId}/in-session/trigger-assessment`, { assessmentKey: testKey }, token);
      setToast({ message: `${testKey.toUpperCase()} sent to client. They'll see it on their screen.`, severity: 'success' });
    } catch (err) {
      setToast({
        message: err?.response?.data?.error || err.message || 'Could not send assessment',
        severity: 'error',
      });
    } finally {
      setPending(prev => ({ ...prev, [testKey]: false }));
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress size={22} /></Box>;
  if (error)   return <Alert severity="info" sx={{ m: 2 }}>{error}</Alert>;
  if (!liveCallId) {
    return <Alert severity="info" sx={{ m: 2 }}>Tools are available once the call is connected.</Alert>;
  }
  if (tests.length === 0) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        No assessments available. Ask your supervisor or admin to grant assessments to your profile.
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 1.5 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, px: 0.5 }}>
        <AssignmentIcon fontSize="small" color="primary" />
        <Typography variant="subtitle2">Push assessment to client</Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 0.5, mb: 1 }}>
        The client will see the questionnaire on their screen. Results post back here automatically.
      </Typography>
      <List dense disablePadding>
        {tests.map(t => (
          <ListItem key={t.testKey} sx={{ px: 1, py: 0.5 }}
            secondaryAction={
              <Button
                size="small"
                variant="outlined"
                startIcon={<SendIcon fontSize="small" />}
                disabled={!!pending[t.testKey]}
                onClick={() => send(t.testKey)}
              >
                {pending[t.testKey] ? 'Sending…' : 'Send'}
              </Button>
            }
          >
            <ListItemText
              primary={
                <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                  <Typography variant="body2" fontWeight={500}>
                    {t.shortName || t.testKey.toUpperCase()}
                  </Typography>
                  {t.population && (
                    <Chip size="small" label={t.population} variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                  )}
                </Stack>
              }
              secondary={
                <Typography variant="caption" color="text.secondary" noWrap>
                  {t.fullName || t.description}
                </Typography>
              }
            />
          </ListItem>
        ))}
      </List>
      <Snackbar
        open={!!toast}
        autoHideDuration={3500}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast && (
          <Alert severity={toast.severity || 'info'} onClose={() => setToast(null)} sx={{ width: '100%' }}>
            {toast.message}
          </Alert>
        )}
      </Snackbar>
    </Box>
  );
}

/**
 * Read-only clinical context panel shown to the therapist during a call.
 * Tabs for Summary (latest conversation), Goals, Check-ins, Pre-Session,
 * and Tools (push an assessment to the client).
 *
 * Data is fetched lazily on first view of each tab to keep the call page snappy.
 */
export default function CallClinicalPanel({ clientId, conversationId, liveCallId, token }) {
  const [tab, setTab] = useState(0);

  return (
    <Paper elevation={1} sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 320 }}>
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ borderBottom: 1, borderColor: 'divider', minHeight: 40 }}
      >
        <Tab label="Summary" sx={{ minHeight: 40 }} />
        <Tab label="Goals" sx={{ minHeight: 40 }} />
        <Tab label="Check-ins" sx={{ minHeight: 40 }} />
        <Tab label="Pre-Session" sx={{ minHeight: 40 }} />
        <Tab label="Tools" sx={{ minHeight: 40 }} />
      </Tabs>
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {tab === 0 && <SummaryTab conversationId={conversationId} token={token} />}
        {tab === 1 && <GoalsTab clientId={clientId} token={token} />}
        {tab === 2 && <CheckInsTab clientId={clientId} token={token} />}
        {tab === 3 && <PreSessionTab clientId={clientId} token={token} />}
        {tab === 4 && <ToolsTab liveCallId={liveCallId} token={token} />}
      </Box>
    </Paper>
  );
}
